import { useEffect, useMemo, useState } from "react";
import { Sparkles, Zap, ShieldCheck, MessagesSquare, ArrowRight, Star, Check, X, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AGENTS } from "@/lib/agents";
import { publicApi } from "@/lib/api";
import ProductReel from "@/components/ProductReel";
import AuthDialog from "@/components/AuthDialog";

const ROTATING = ["draft posts.", "chase leads.", "keep the books.", "spar on ideas.", "ship the launch."];

const DEMO_CHAT = [
  { who: "you",   text: "i think i have an idea but i don't know if it's dumb" },
  { who: "casey", text: "run it by me. one line — what would you sell, to whom?" },
  { who: "you",   text: "reusable coffee cups for cafes in berlin" },
  { who: "casey", text: "ok. i can name 3 cafes we should talk to first. want me to draft the pitch?" },
];

const TESTIMONIALS = [
  { name: "Maya · handmade candles", quote: "Rex booked 4 wholesale calls while I slept.", color: "#00FFFF" },
  { name: "Dev · solo devtool",       quote: "Casey talked me out of building the wrong feature. saved a week.", color: "#C0C4CC" },
  { name: "Iris · yoga studio",        quote: "Ada flagged my runway before my bank app did.", color: "#B026FF" },
  { name: "Kojo · newsletter",         quote: "Mica made a launch poster i actually posted.", color: "#FF007F" },
];

const MARQUEE_TAGS = [
  "✦ no more forms",
  "✦ ai that actually ships",
  "✦ 3 employees. 1 you.",
  "✦ zero cofounder drama",
  "✦ calmer money screens",
  "✦ approve with one tap",
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Kept for cases where an inline "Continue with Google" is used from a sub-component that
// does not have access to the dialog trigger.
const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Landing() {
  const [rot, setRot] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("signin");
  const openAuth = (tab = "signin") => { setAuthTab(tab); setAuthOpen(true); };
  useEffect(() => { const t = setInterval(() => setRot((r) => (r + 1) % ROTATING.length), 2200); return () => clearInterval(t); }, []);

  // Marquee needs each item twice for the -50% x-loop, computed once.
  const marqueeItems = useMemo(
    () => [...MARQUEE_TAGS, ...MARQUEE_TAGS].map((t, idx) => ({ id: `${idx}-${t}`, text: t })),
    []
  );

  return (
    <div className="min-h-screen aurora relative overflow-hidden grain">
      <FloatingStickers />

      <header className="relative z-20 flex items-center justify-between px-8 py-5" data-testid="landing-header">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-neon-pink animate-pulse" />
          <span className="font-display text-2xl font-bold chrome-text">SoloCrew</span>
        </div>
        <div className="flex items-center gap-2">
          <a href="#how" className="hidden sm:block text-white/70 hover:text-white font-semibold px-3">How it works</a>
          <a href="#crew" className="hidden sm:block text-white/70 hover:text-white font-semibold px-3">The crew</a>
          <a href="#pricing" className="hidden sm:block text-white/70 hover:text-white font-semibold px-3">Pricing</a>
          <Button data-testid="sign-in-header-btn" onClick={() => openAuth("signin")} className="rounded-full bg-white text-slate-900 hover:bg-neon-cyan font-bold px-5">
            Sign in
          </Button>
        </div>
      </header>

      {/* marquee */}
      <div className="relative z-10 border-y border-white/10 bg-black/30 overflow-hidden py-2" data-testid="landing-marquee">
        <motion.div
          className="flex gap-10 whitespace-nowrap font-mono text-xs uppercase tracking-[0.25em] text-white/70"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        >
          {marqueeItems.map((it) => (
            <span key={it.id}>{it.text}</span>
          ))}
        </motion.div>
      </div>

      <section className="relative z-10 max-w-6xl mx-auto px-8 pt-14 pb-12 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="font-mono text-xs uppercase tracking-widest text-neon-cyan mb-4"
          >
            ✦ built for solo founders
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mb-6"
          >
            <span className="chrome-text">The whole team.</span>
            <br />
            <span className="text-white">One founder.</span>
          </motion.h1>

          <div className="text-white/85 text-lg max-w-2xl mb-3 flex flex-wrap items-baseline gap-2">
            Your AI crew will
            <AnimatePresence mode="wait">
              <motion.span
                key={rot}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="font-display font-bold text-neon-pink"
              >
                {ROTATING[rot]}
              </motion.span>
            </AnimatePresence>
          </div>
          <p className="text-white/70 text-base max-w-xl mb-8">
            You approve the risky stuff. They handle the rest. No dashboards to babysit.
          </p>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, type: "spring" }} className="flex flex-wrap gap-3 mb-6">
            <Button data-testid="sign-in-cta-btn" onClick={() => openAuth("signup")} className="rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold px-7 py-6 text-base neon-glow-pink">
              Start free
            </Button>
            <a href="#reel" className="rounded-full px-6 py-3 border border-white/20 hover:border-white/40 text-white font-semibold flex items-center gap-2" data-testid="see-demo-link">
              See it move <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>

          <a href="#waitlist" className="text-sm text-white/60 hover:text-white/90 underline underline-offset-4 decoration-neon-cyan/50 mb-4 inline-block" data-testid="hero-waitlist-link">
            or just drop an email — no Google yet →
          </a>

          <div className="flex items-center gap-3 text-sm text-white/60">
            <div className="flex -space-x-2">
              {Object.values(AGENTS).map((a) => (
                <img key={a.id} src={a.avatar} alt="" className="w-7 h-7 rounded-full border-2 border-[#0F0524] object-cover" />
              ))}
            </div>
            <span>4 personas, ready in ~10 min of chat.</span>
          </div>
        </div>

        {/* live-feeling chat mock */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: -2 }}
          className="lg:col-span-5 glass rounded-[2rem] p-5 relative sticker"
          data-testid="landing-chat-demo"
          style={{ boxShadow: "0 0 60px rgba(255,0,127,0.35)" }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 slime-blob overflow-hidden" style={{ background: AGENTS.ceo.gradient }}>
              <img src={AGENTS.ceo.avatar} alt="Casey" className="w-full h-full object-cover mix-blend-luminosity" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neon-chrome">Casey · CEO</div>
              <div className="font-display text-lg font-bold chrome-text">Live in your workspace</div>
            </div>
            <span className="ml-auto flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-neon-lime">
              <span className="w-2 h-2 rounded-full bg-neon-lime animate-pulse" /> online
            </span>
          </div>

          <ChatReplay />
        </motion.div>
      </section>

      {/* stats */}
      <section className="relative z-10 max-w-6xl mx-auto px-8 grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="landing-stats">
        {[
          { k: "3", v: "AI employees", c: "text-neon-pink" },
          { k: "1", v: "founder (you)", c: "text-neon-cyan" },
          { k: "0", v: "meetings",     c: "text-neon-lime" },
          { k: "10m", v: "to spin up", c: "text-neon-tangerine" },
        ].map((s) => (
          <motion.div key={s.k} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-5 text-center">
            <div className={"font-display text-5xl font-bold " + s.c}>{s.k}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mt-1">{s.v}</div>
          </motion.div>
        ))}
      </section>

      {/* product reel */}
      <ProductReel />

      {/* meet the crew */}
      <section id="crew" className="relative z-10 max-w-6xl mx-auto px-8 mb-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-display text-4xl sm:text-5xl font-bold mb-2">
          <span className="chrome-text">Meet the crew.</span>
        </motion.h2>
        <p className="text-white/70 mb-8">Four personas. Four workspaces. One founder in charge.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {Object.values(AGENTS).map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, rotate: [-1, 1, 0][i % 3] }}
              transition={{ delay: i * 0.08, type: "spring", bounce: 0.4 }}
              className="glass rounded-3xl p-5 relative overflow-hidden"
              data-testid={`agent-card-${a.id}`}
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 blur-3xl opacity-40" style={{ background: a.gradient }} />
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
                className="w-20 h-20 slime-blob overflow-hidden mb-3 relative"
                style={{ background: a.gradient, boxShadow: `0 0 30px ${a.color}66` }}
              >
                <img src={a.avatar} alt={a.name} className="w-full h-full object-cover mix-blend-luminosity opacity-95" />
              </motion.div>
              <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: a.color }}>{a.role}</div>
              <div className="font-display text-2xl font-bold mt-1">{a.name}</div>
              <p className="text-sm text-white/70 mt-2">{a.tagline}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* how it works */}
      <section id="how" className="relative z-10 max-w-6xl mx-auto px-8 mb-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-display text-4xl sm:text-5xl font-bold mb-8">
          <span className="chrome-text">How it works.</span>
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { n: "01", t: "Chat with Casey", b: "A 10-min conversation. No forms, no dropdowns. Casey figures out your stage.", c: "#C0C4CC" },
            { n: "02", t: "Your crew shows up", b: "Mica, Rex, and Ada each pitch 3 things they'll do this week.",              c: "#FF007F" },
            { n: "03", t: "Approve or edit",   b: "Risky stuff waits in your inbox. One tap. Game-feel, not paperwork.",       c: "#39FF14" },
          ].map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass rounded-3xl p-6 relative">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Step</div>
              <div className="font-display text-6xl font-bold mb-2" style={{ color: s.c, textShadow: `0 0 20px ${s.c}66` }}>{s.n}</div>
              <div className="font-display text-xl font-bold">{s.t}</div>
              <p className="text-white/70 text-sm mt-2">{s.b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* live approval demo */}
      <section id="demo" className="relative z-10 max-w-4xl mx-auto px-8 mb-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-display text-4xl sm:text-5xl font-bold mb-3 text-center">
          <span className="chrome-text">One tap. Done.</span>
        </motion.h2>
        <p className="text-white/70 mb-8 text-center">This is what an actual approval feels like — <em>not</em> a Trapper Keeper of paperwork.</p>
        <ApprovalCardDemo />
      </section>

      {/* testimonials */}
      <section className="relative z-10 max-w-6xl mx-auto px-8 mb-20">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold"><span className="chrome-text">Founders who ship.</span></h2>
          <div className="flex items-center gap-1 text-neon-tangerine">
            {["s1", "s2", "s3", "s4", "s5"].map((k) => <Star key={k} className="w-4 h-4 fill-current" />)}
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 20, rotate: [-3, 2, -1, 3][i] }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -6, rotate: 0 }}
              transition={{ delay: i * 0.08, type: "spring" }}
              className="sticker bg-white text-slate-900 p-4"
              style={{ transform: `rotate(${[-3, 2, -1, 3][i]}deg)` }}
              data-testid={`testimonial-${i}`}
            >
              <p className="font-display text-lg leading-snug mb-3">"{t.quote}"</p>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full" style={{ background: t.color }} />
                <div className="text-xs font-mono uppercase tracking-widest text-slate-600">{t.name}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* pricing */}
      <section id="pricing" className="relative z-10 max-w-6xl mx-auto px-8 mb-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-display text-4xl sm:text-5xl font-bold mb-2">
          <span className="chrome-text">Pricing that respects broke.</span>
        </motion.h2>
        <p className="text-white/70 mb-8">Start free forever. Upgrade the day your crew makes you money.</p>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            {
              name: "Solo", price: "$0", cadence: "forever", tag: "start here", color: "#C0C4CC",
              bullets: ["Casey (CEO chat)", "1 agent of your choice", "5 approvals / week", "Community"],
              cta: "Start free", primary: false,
            },
            {
              name: "Crew", price: "$29", cadence: "/ month", tag: "most solo founders", color: "#FF007F",
              bullets: ["All 3 agents unlocked", "Unlimited approvals", "Nano Banana visuals", "WhatsApp bridge (soon)"],
              cta: "Get the crew", primary: true,
            },
            {
              name: "Studio", price: "$99", cadence: "/ month", tag: "already earning", color: "#39FF14",
              bullets: ["Everything in Crew", "Priority AI (Sonnet 5)", "CPA marketplace access", "Founder concierge"],
              cta: "Level up", primary: false,
            },
          ].map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30, rotate: [-2, 0, 2][i] }}
              whileInView={{ opacity: 1, y: 0, rotate: [-2, 0, 2][i] }}
              whileHover={{ y: -8, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, type: "spring", bounce: 0.35 }}
              className={"relative glass rounded-[2rem] p-6 sticker " + (p.primary ? "neon-glow-pink" : "")}
              style={{ boxShadow: p.primary ? `0 0 60px ${p.color}66` : undefined }}
              data-testid={`pricing-${p.name.toLowerCase()}`}
            >
              {p.primary && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-neon-pink text-white font-mono text-[10px] uppercase tracking-widest neon-glow-pink">
                  ★ most popular
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: p.color }}>{p.tag}</div>
              <div className="font-display text-3xl font-bold mt-1">{p.name}</div>
              <div className="flex items-baseline gap-2 mt-3 mb-4">
                <span className="font-display text-5xl font-bold" style={{ color: p.color }}>{p.price}</span>
                <span className="text-white/60 text-sm">{p.cadence}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-white/85">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.color }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                onClick={() => openAuth("signup")}
                className={"w-full rounded-full font-bold py-6 " + (p.primary ? "bg-neon-pink hover:bg-neon-pink/90 text-white neon-glow-pink" : "bg-white/10 hover:bg-white/20 text-white border border-white/20")}
              >
                {p.cta}
              </Button>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-white/50 font-mono uppercase tracking-widest mt-6">
          ✦ cancel anytime · no card to start · every plan includes casey ✦
        </p>
      </section>

      {/* waitlist */}
      <section id="waitlist" className="relative z-10 max-w-4xl mx-auto px-8 mb-20">
        <WaitlistCard />
      </section>

      {/* big cta */}
      <section className="relative z-10 max-w-4xl mx-auto px-8 pb-24">
        <div className="glass rounded-[2.5rem] p-10 text-center relative overflow-hidden">
          <div className="absolute -top-16 -left-16 w-56 h-56 bg-neon-pink/40 blur-3xl slime-blob" />
          <div className="absolute -bottom-16 -right-16 w-56 h-56 bg-neon-cyan/40 blur-3xl slime-blob" />
          <h2 className="font-display text-4xl sm:text-5xl font-bold relative"><span className="chrome-text">Your crew's waiting.</span></h2>
          <p className="text-white/70 mt-3 mb-6 relative">Sign in, chat with Casey for 10 minutes, watch the rest fall into place.</p>
          <Button data-testid="sign-in-footer-btn" onClick={() => openAuth("signup")} className="rounded-full bg-neon-lime hover:bg-neon-lime/90 text-slate-900 font-bold px-8 py-6 text-base neon-glow-lime relative">
            Start free — no credit card
          </Button>
        </div>
        <div className="text-center text-xs text-white/40 font-mono uppercase tracking-widest mt-8">
          ✦ SoloCrew · handmade for solo bootstrappers ✦
        </div>
      </section>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} />
    </div>
  );
}

function FloatingStickers() {
  const stickers = [
    { top: "12%", left: "6%",  emoji: "✦", size: 24, delay: 0 },
    { top: "22%", right: "8%", emoji: "◆", size: 20, delay: 0.4 },
    { top: "60%", left: "4%",  emoji: "✧", size: 28, delay: 0.8 },
    { top: "78%", right: "5%", emoji: "❋", size: 22, delay: 1.2 },
    { top: "38%", left: "48%", emoji: "✦", size: 18, delay: 0.2 },
  ];
  return (
    <>
      {stickers.map((s) => (
        <motion.span
          key={`${s.top}-${s.emoji}-${s.left ?? s.right}`}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0.5, 1, 0.5], y: [0, -15, 0], scale: 1 }}
          transition={{ duration: 4, repeat: Infinity, delay: s.delay }}
          className="pointer-events-none absolute z-0 text-neon-pink"
          style={{ top: s.top, left: s.left, right: s.right, fontSize: s.size }}
          aria-hidden
        >
          {s.emoji}
        </motion.span>
      ))}
    </>
  );
}

function ChatReplay() {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setVisible((v) => (v + 1) % (DEMO_CHAT.length + 2)), 1600);
    return () => clearInterval(t);
  }, []);
  const shown = DEMO_CHAT.slice(0, Math.min(visible, DEMO_CHAT.length));

  return (
    <div className="space-y-2 min-h-[240px]">
      {shown.map((m, i) => (
        <motion.div
          key={i + "-" + m.text}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className={m.who === "you" ? "flex justify-end" : "flex justify-start"}
        >
          <div className={
            "max-w-[85%] rounded-2xl px-3.5 py-2 text-sm font-medium " +
            (m.who === "you"
              ? "bg-neon-pink text-white rounded-br-sm"
              : "bg-white/10 text-white border border-white/15 rounded-bl-sm")
          }>
            {m.text}
          </div>
        </motion.div>
      ))}
      {visible >= DEMO_CHAT.length && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-2 flex items-center gap-2 text-xs text-white/60 font-mono">
          <span className="w-1.5 h-1.5 rounded-full bg-neon-lime animate-pulse" /> casey queued 3 outreach drafts for rex ↗
        </motion.div>
      )}
    </div>
  );
}

function WaitlistCard() {
  const [email, setEmail] = useState("");
  const [count, setCount] = useState(null);
  const [phase, setPhase] = useState("form"); // form | question | done
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null); // {referral_code, position, referrals_count}
  const [idea, setIdea] = useState("");

  // read ?ref= from URL, stash it
  const refCode = new URLSearchParams(window.location.search).get("ref");

  useEffect(() => { publicApi.waitlistCount().then(({ data }) => setCount(data.count)).catch(() => {}); }, []);

  const shareUrl = me?.referral_code
    ? `${window.location.origin}/?ref=${me.referral_code}#waitlist`
    : "";

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    const clean = email.trim().toLowerCase();
    if (!clean.includes("@") || clean.length < 5) return toast.error("That doesn't look like an email");
    setLoading(true);
    try {
      const { data } = await publicApi.joinWaitlist(clean, "landing", refCode);
      setMe(data);
      setPhase(data.needs_details ? "question" : "done");
      toast.success(data.already ? "You're already on the list ✦" : `You're #${data.position} ✦`);
      publicApi.waitlistCount().then(({ data }) => setCount(data.count)).catch(() => {});
    } catch {
      toast.error("Something bounced. Try again in a sec.");
    } finally {
      setLoading(false);
    }
  };

  const submitIdea = async (e) => {
    e?.preventDefault?.();
    if (!idea.trim()) { setPhase("done"); return; }
    setLoading(true);
    try {
      await publicApi.waitlistDetails(email.trim().toLowerCase(), idea.trim());
      setPhase("done");
    } catch { setPhase("done"); }
    finally { setLoading(false); }
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied ✦ send it to a friend"); }
    catch { toast.error("Couldn't copy — long-press the link"); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="glass rounded-[2rem] p-8 relative overflow-hidden sticker"
      style={{ boxShadow: "0 0 60px rgba(0,255,255,0.25)" }}
      data-testid="waitlist-card"
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-neon-cyan/30 blur-3xl slime-blob" />
      <div className="relative grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neon-cyan mb-2">
            {refCode ? "✦ your friend sent you" : "✦ not ready yet?"}
          </div>
          <h3 className="font-display text-3xl sm:text-4xl font-bold mb-2">
            <span className="chrome-text">{refCode ? "Skip the line." : "Drop your email."}</span>
          </h3>
          <p className="text-white/70">
            {refCode
              ? "You were referred — you jump 3 spots ahead. Just drop your email."
              : "We'll ping you when your crew has room. No spam. No newsletter. Just one nudge."}
          </p>
          {count !== null && count > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-xs font-mono text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-lime animate-pulse" />
              <span data-testid="waitlist-count">{count}</span> founders waiting
            </div>
          )}
        </div>

        <AnimatePresence mode="wait">
          {phase === "form" && (
            <motion.form
              key="form"
              onSubmit={submitEmail}
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                <Input type="email" data-testid="waitlist-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@yourthing.co"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-full h-14 pl-11 pr-4 text-base" required />
              </div>
              <Button data-testid="waitlist-submit" type="submit" disabled={loading}
                className="rounded-full bg-neon-cyan hover:bg-neon-cyan/90 text-slate-900 font-bold px-6 h-14 neon-glow-cyan">
                {loading ? "Adding…" : "Join waitlist"}
              </Button>
            </motion.form>
          )}

          {phase === "question" && (
            <motion.form
              key="question"
              onSubmit={submitIdea}
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="space-y-3"
              data-testid="waitlist-question"
            >
              <div className="text-sm font-mono uppercase tracking-widest text-neon-pink">✦ one tiny question</div>
              <label className="font-display text-lg font-bold block">What are you building?</label>
              <Textarea data-testid="waitlist-idea-input" value={idea} onChange={(e) => setIdea(e.target.value)}
                placeholder="e.g. reusable coffee cups for cafes in berlin"
                className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-2xl min-h-[80px]" />
              <div className="flex gap-2">
                <Button data-testid="waitlist-idea-skip" type="button" variant="ghost" onClick={() => setPhase("done")}
                  className="rounded-full text-white hover:bg-white/10">Skip</Button>
                <Button data-testid="waitlist-idea-submit" type="submit" disabled={loading}
                  className="ml-auto rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold neon-glow-pink px-6">
                  Save & see my spot
                </Button>
              </div>
            </motion.form>
          )}

          {phase === "done" && me && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="glass rounded-2xl p-5 border-2 border-neon-lime/50"
              data-testid="waitlist-success"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-neon-lime/20 flex items-center justify-center neon-glow-lime">
                  <Check className="w-5 h-5 text-neon-lime" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neon-lime">you're in</div>
                  <div className="font-display text-2xl font-bold">
                    #<span data-testid="waitlist-position">{me.position}</span> in line
                  </div>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-3">
                Refer 3 friends and skip <span className="text-neon-lime font-bold">9 spots</span>.
                {me.referrals_count > 0 && <> You've referred <b className="text-neon-lime">{me.referrals_count}</b> already ✦</>}
              </p>
              <div className="flex gap-2 items-center bg-slate-950/60 border border-white/10 rounded-full pl-4 pr-1 py-1">
                <span className="font-mono text-xs text-white/70 truncate flex-1" data-testid="waitlist-share-url">{shareUrl}</span>
                <Button data-testid="waitlist-copy-share" type="button" onClick={copyShare}
                  className="rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold px-4 py-1 h-8 text-xs">
                  Copy link
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function ApprovalCardDemo() {  const [state, setState] = useState("idle"); // idle | approved | rejected
  const auto = () => setTimeout(() => setState("idle"), 1400);
  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={state}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{
            opacity: 1, y: 0, scale: 1,
            x: state === "approved" ? 400 : state === "rejected" ? -400 : 0,
            rotate: state === "approved" ? 12 : state === "rejected" ? -12 : 0,
          }}
          exit={{ opacity: 0 }}
          transition={{ type: "spring", bounce: 0.35 }}
          className="glass sticker rounded-[2rem] p-6 max-w-lg mx-auto"
          style={{ boxShadow: "0 0 60px rgba(255,0,127,0.35)" }}
          data-testid="landing-approval-demo"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 slime-blob overflow-hidden" style={{ background: AGENTS.marketer.gradient }}>
              <img src={AGENTS.marketer.avatar} alt="" className="w-full h-full object-cover mix-blend-luminosity" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neon-pink">Mica · Marketer</div>
              <div className="font-display text-xl font-bold">Draft post: Launch Monday</div>
            </div>
          </div>
          <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 whitespace-pre-wrap font-mono text-sm text-neon-lime/90">
            {"we're launching Loopcup next Monday in Berlin. cafes: your customers keep asking for less waste. pilot with us free for 30 days. reply LOOP to join."}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <Button
              data-testid="demo-reject"
              onClick={() => { setState("rejected"); auto(); }}
              className="rounded-2xl bg-neon-pink hover:bg-neon-pink/90 text-white font-bold py-5 neon-glow-pink"
            >
              <X className="w-5 h-5 mr-1" /> Reject
            </Button>
            <Button
              data-testid="demo-edit"
              onClick={startGoogleAuth}
              className="rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold py-5 border border-white/20"
            >
              Edit
            </Button>
            <Button
              data-testid="demo-approve"
              onClick={() => { setState("approved"); auto(); }}
              className="rounded-2xl bg-neon-lime hover:bg-neon-lime/90 text-slate-900 font-bold py-5 neon-glow-lime"
            >
              <Check className="w-5 h-5 mr-1" /> Approve
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
      {state !== "idle" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mt-4 font-display text-2xl font-bold"
          style={{ color: state === "approved" ? "#39FF14" : "#FF007F" }}
        >
          {state === "approved" ? "✦ Sent to the world" : "Back to the queue"}
        </motion.div>
      )}
    </div>
  );
}
